// A run's re-grade from its page (TOM-257, ADR 0254; specs/api_contracts.md
// §11.5 "Re-grading a stored run"): whether the page offers it and why, the
// request its form posts, and every refusal and failure in plain words. A
// re-grade grades the run's stored replies again with the judges the admin
// picks, into a new run (run kind regrade, graded from this one), on the
// worker, inside a required budget and the daily eval cap. No model under test
// is asked again. Pure: tested in regrade.test.ts.

import { formatUsd, modelShortName } from './format';
import {
  EVAL_LAUNCH_MAX_JUDGES,
  EVAL_NOTES_MAX_CHARS,
  launchErrorText,
  type EvalChartsMode,
  type EvalLaunchEstimate,
  type EvalLaunchOptions,
  type EvalLaunchOutcome,
} from './launch';
import type {
  EvalJudgeFailures,
  EvalRole,
  EvalRunDetail,
  EvalRunKind,
  EvalRunListItem,
  EvalRunStatus,
  EvalSplitFilter,
  EvalSuite,
} from './types';

// ----- POST /v1/admin/evals/runs/:id/regrade(/estimate) ----------------------------------

export interface EvalRegradeRequest {
  judges?: string[];
  noJudge?: boolean;
  judgeTwice?: boolean;
  allowSameFamilyJudge?: boolean;
  confirmTestSplit?: boolean;
  budgetUsd: number;
  notes?: string;
}

/** The stored run a re-grade reads, as its estimate describes it. */
export interface EvalRegradeSource {
  runId: string;
  runKind: EvalRunKind;
  status: EvalRunStatus;
  suite: EvalSuite;
  split: EvalSplitFilter | null;
  charts: EvalChartsMode;
  /** Results stored, and of those the ones the re-grade copies and grades. */
  results: number;
  regradable: number;
  /** Left out: a case no longer in a corpus, or a long case without its moment. */
  skipped: number;
  /** Model rows that hold fewer results than they planned (a run cut short). */
  short: { model: string; role: EvalRole; planned: number; written: number }[];
}

/** A re-grade's plan and estimated cost: a launch's, less what only a launch has. */
export type EvalRegradeEstimate = Omit<EvalLaunchEstimate, 'split' | 'failedReads'> & {
  source: EvalRegradeSource;
};

export interface EvalRegradeOutcome extends EvalLaunchOutcome {
  gradedFromRunId: string;
}

// ----- Whether the page offers it ----------------------------------------------------------

/**
 * How a run's page offers a re-grade. `none`: it cannot be one (a
 * calibration re-grade, or a run still going). `wait`: a run that stalled,
 * which the eval sweep closes first. `offer`: a closed run; `needed` when some
 * of its results were never graded, or a model was not graded at all, and
 * `lines` say why.
 */
export type EvalRegradeOffer =
  | { kind: 'none' }
  | { kind: 'wait'; text: string }
  | { kind: 'nothing'; text: string }
  | { kind: 'offer'; needed: boolean; lines: string[] };

const LIVE: readonly EvalRunStatus[] = ['queued', 'running', 'cancelling'];

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const OUT_OF_CREDIT = 'its provider is out of credit';
const NOT_SERVED = 'its provider does not serve that model';
const KEY_REFUSED = 'its provider refused the key';

/**
 * The HTTP status a failed judge call's reason names: the runner's
 * `HTTP 402: …` (cloud runner.ts neoEvalJudgeCallErrorText), the OpenRouter
 * client's `… returned 402: …`, or the Anthropic SDK's leading `400 {…}`.
 * Null when it names none.
 */
export function judgeFailureStatus(text: string): number | null {
  const m =
    /^HTTP (\d{3})\b/.exec(text) ?? /\breturned (\d{3})\b/.exec(text) ?? /^(\d{3}) /.exec(text);
  return m === null ? null : Number(m[1]);
}

/**
 * What a failed judge call's reason means, in plain words (TOM-257). The
 * status decides when the reason names one; the words of the message decide
 * only for a 400 (a provider's catch-all, Anthropic's "credit balance is too
 * low" among them) or when there is no status.
 */
export function judgeFailureCause(reason: string): string {
  const r = reason.replace(/^[\s\S]*?judge call failed:\s*/i, '').trim();
  const short = r.length > 160 ? `${r.slice(0, 159)}…` : r;
  const failed = short.length > 0 ? `the call failed (${short})` : 'the call failed';
  const words = (): string | null => {
    if (/credit/i.test(r)) return OUT_OF_CREDIT;
    if (/not a valid model|no such model|model.{0,40}not (found|exist)/i.test(r)) return NOT_SERVED;
    return null;
  };
  const status = judgeFailureStatus(r);
  if (status !== null) {
    if (status === 402) return OUT_OF_CREDIT;
    if (status === 401) return KEY_REFUSED;
    // OpenRouter answers 403 for a flagged moderation check, which judging an
    // attack's reply can trip, and for a key over its limit.
    if (status === 403) return 'its provider refused the call (moderation or a key limit)';
    if (status === 404) return NOT_SERVED;
    if (status === 408) return 'it did not answer in time';
    if (status === 429) return 'its provider limited how often it could be called';
    if (status >= 500) return 'its provider was down';
    return (status === 400 ? words() : null) ?? failed;
  }
  const said = words();
  if (said !== null) return said;
  if (/unauthori[sz]ed|authentication|invalid.{0,10}api key/i.test(r)) return KEY_REFUSED;
  if (/rate.?limit/i.test(r)) return 'its provider limited how often it could be called';
  if (/timed? ?out|timeout|aborted/i.test(r)) return 'it did not answer in time';
  if (/overloaded|service unavailable/i.test(r)) return 'its provider was down';
  return failed;
}

/** What the admin can do about a judge failure, after its cause. */
function judgeFailureAdvice(reasons: EvalJudgeFailures['reasons']): string {
  const causes = reasons.map((r) => judgeFailureCause(r.reason));
  if (causes.includes(OUT_OF_CREDIT)) {
    return 'Add credit with that provider, or re-grade with a judge from another provider.';
  }
  if (causes.some((c) => c === NOT_SERVED || c === KEY_REFUSED || c.includes('refused the call'))) {
    return 'Re-grade this run with another judge.';
  }
  return 'Re-grade this run to finish its grading.';
}

/**
 * Why a run's results were never graded, one line each: the judge failures
 * by judge and cause, then any other ungraded results.
 */
export function judgeFailureLines(failures: EvalJudgeFailures, ungraded: number): string[] {
  const lines: string[] = [];
  if (failures.results > 0) {
    const named = failures.reasons.reduce((s, r) => s + r.results, 0);
    for (const r of failures.reasons) {
      const judge = r.judge !== null ? `The judge ${modelShortName(r.judge)}` : 'The judge';
      lines.push(
        `${judge} failed on ${plural(r.results, 'result', 'results')}: ${judgeFailureCause(r.reason)}.`
      );
    }
    if (named < failures.results) {
      const rest = failures.results - named;
      lines.push(
        failures.reasons.length === 0
          ? `The judge failed on ${plural(rest, 'result', 'results')}.`
          : `It failed on ${plural(rest, 'more result', 'more results')} too.`
      );
    }
    lines.push(judgeFailureAdvice(failures.reasons));
  }
  const other = ungraded - failures.results;
  if (other > 0) {
    lines.push(
      `${plural(other, 'result was', 'results were')} collected but never graded (the run stopped before grading ${other === 1 ? 'it' : 'them'}).`
    );
  }
  return lines;
}

/**
 * Whether and how a run's page offers a re-grade, and what it says. A run
 * whose grading a re-grade already finished, or one a re-grade is grading
 * now, still offers it, but not as the page's first action.
 */
export function regradeOffer(
  detail: Pick<EvalRunDetail, 'run' | 'models' | 'judgeFailures' | 'regrades'>
): EvalRegradeOffer {
  const { run, models, judgeFailures, regrades } = detail;
  if (run.runKind === 'calibration') return { kind: 'none' };
  if (LIVE.includes(run.status)) {
    return run.stalled
      ? {
          kind: 'wait',
          text: 'This run has stalled: nothing has worked on it for a while. The eval sweep closes it as incomplete at its next pass, and then it can be re-graded.',
        }
      : { kind: 'none' };
  }
  // A closed run that stored no reply: the server refuses it (no_results).
  if (models.every((m) => m.written === 0)) {
    return {
      kind: 'nothing',
      text: 'Nothing stored to re-grade: this run holds no replies.',
    };
  }
  const ungraded = models.reduce((s, m) => s + Math.max(0, m.written - m.complete), 0);
  const lines = judgeFailureLines(judgeFailures, ungraded);
  const notGraded = models.filter((m) => m.grading === 'none');
  for (const m of notGraded) {
    lines.push(
      `${modelShortName(m.model)}${m.role === 'watch' ? ' (Watch)' : ''} was not graded: no judge could grade it. Re-grade with a judge that can, or allow a judge from its own vendor.`
    );
  }
  const needed = lines.length > 0;
  const heuristic = !needed && models.length > 0 && models.every((m) => m.grading === 'heuristic');
  // A re-grade that grades with a judge: one with no judge leaves the heuristic's verdicts.
  const judged = (r: EvalRunListItem) => r.models.some((m) => m.judgeModel !== null);
  if (needed || heuristic) {
    const answers = regrades.filter((r) => needed || judged(r));
    if (answers.some((r) => LIVE.includes(r.status) && !r.stalled)) {
      lines.push('A re-grade of this run is under way: see it below.');
      return { kind: 'offer', needed: false, lines };
    }
    if (answers.some((r) => r.status === 'succeeded')) {
      lines.push(
        needed
          ? 'A re-grade of this run has finished its grading: see it below.'
          : 'A re-grade of this run graded it with a judge: see it below.'
      );
      return { kind: 'offer', needed: false, lines };
    }
    // A stalled re-grade answers nothing: the run still needs one.
    if (answers.some((r) => LIVE.includes(r.status) && r.stalled)) {
      lines.push(
        'A re-grade of this run has stalled: nothing has worked on it for a while. The eval sweep closes it; re-grade this run again.'
      );
    }
  }
  if (heuristic) {
    lines.push('Graded by the phrase heuristic, not by a judge: re-grade it with a judge.');
  }
  return { kind: 'offer', needed, lines };
}

// ----- The form ------------------------------------------------------------------------------

/** What the re-grade form holds. The budget stays text until the request is built. */
export interface EvalRegradeForm {
  extraJudges: string[];
  judges: string[];
  noJudge: boolean;
  judgeTwice: boolean;
  allowSameFamilyJudge: boolean;
  /** The test split's second confirmation, for a run that holds held-out test cases. */
  confirmTestSplit: boolean;
  budgetUsd: string;
  notes: string;
}

/** The form as it opens: the owner's judges, judging twice as the run's suite defaults to. */
export function defaultRegradeForm(
  options: Pick<EvalLaunchOptions, 'judges' | 'defaults'>,
  suite: EvalSuite
): EvalRegradeForm {
  return {
    extraJudges: [],
    judges: options.judges.map((j) => j.id),
    noJudge: false,
    judgeTwice: options.defaults.judgeTwice[suite],
    allowSameFamilyJudge: false,
    confirmTestSplit: false,
    budgetUsd: '',
    notes: '',
  };
}

const sameList = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((x, i) => x === b[i]);

/**
 * The request the form posts, or what is wrong with it. Keys the server
 * defaults are left out when the form holds the default, so the audit row
 * records what the admin changed.
 */
export function regradeRequestFromForm(
  form: EvalRegradeForm,
  options: Pick<EvalLaunchOptions, 'judges' | 'defaults' | 'maxBudgetUsd'>,
  suite: EvalSuite
): { ok: true; body: EvalRegradeRequest } | { ok: false; problems: string[] } {
  const problems: string[] = [];
  const budget = Number(form.budgetUsd);
  if (
    form.budgetUsd.trim() === '' ||
    !Number.isFinite(budget) ||
    budget <= 0 ||
    budget > options.maxBudgetUsd
  ) {
    problems.push(
      `Set the re-grade's budget in USD: more than $0 and at most ${formatUsd(options.maxBudgetUsd)}.`
    );
  }
  if (!form.noJudge && form.judges.length === 0) {
    problems.push('Choose a judge, or grade with the phrase heuristic (no judge).');
  }
  if (!form.noJudge && form.judges.length > EVAL_LAUNCH_MAX_JUDGES) {
    problems.push(`Choose at most ${EVAL_LAUNCH_MAX_JUDGES} judges.`);
  }
  const notes = form.notes.trim();
  if (notes.length > EVAL_NOTES_MAX_CHARS) {
    problems.push(`Notes are at most ${EVAL_NOTES_MAX_CHARS} characters.`);
  }
  if (problems.length > 0) return { ok: false, problems };
  const body: EvalRegradeRequest = {
    ...(form.noJudge
      ? { noJudge: true }
      : {
          ...(sameList(
            form.judges,
            options.judges.map((j) => j.id)
          )
            ? {}
            : { judges: [...form.judges] }),
          ...(form.judgeTwice !== options.defaults.judgeTwice[suite]
            ? { judgeTwice: form.judgeTwice }
            : {}),
          ...(form.allowSameFamilyJudge ? { allowSameFamilyJudge: true } : {}),
        }),
    ...(form.confirmTestSplit ? { confirmTestSplit: true } : {}),
    budgetUsd: budget,
    ...(notes.length > 0 ? { notes } : {}),
  };
  return { ok: true, body };
}

/** A request's identity, to tell whether an estimate is for the form as it stands. */
export function regradeRequestKey(body: EvalRegradeRequest): string {
  return JSON.stringify(body);
}

/** What the estimate says the re-grade grades: the stored results it copies, and what it leaves. */
export function regradeSourceText(s: EvalRegradeSource): string {
  const parts = [`Grades ${plural(s.regradable, 'stored reply', 'stored replies')} again`];
  if (s.skipped > 0) {
    parts.push(
      `${plural(s.skipped, 'result is', 'results are')} left out (a case the eval no longer has, or a long case without its moment)`
    );
  }
  // The source's shortfall (ADR 0254 §2): what it planned and never stored.
  const short =
    s.short.length === 0
      ? ''
      : ` The run stopped short: ${s.short
          .map(
            (m) =>
              `${modelShortName(m.model)}${m.role === 'watch' ? ' (Watch)' : ''} holds ${m.written} of the ${m.planned} replies it planned`
          )
          .join(', ')}, so only what it holds is graded.`;
  return `${parts.join('; ')}.${short} No model is asked again: only the judges are called.`;
}

/** The estimate is more than the budget: the re-grade may stop before it grades everything. */
export function regradeOverBudgetText(e: EvalRegradeEstimate): string | null {
  if (e.estimate.totalUsd <= e.budgetUsd) return null;
  return `The estimate (${formatUsd(e.estimate.totalUsd)}) is more than the budget (${formatUsd(e.budgetUsd)}). The re-grade stops before a reply that could cross the budget, so some replies may stay ungraded. Raise the budget to grade them all.`;
}

// ----- Failures in plain words ----------------------------------------------------------------

/** Why a re-grade or its estimate was refused, from the error body the route answered. */
export function regradeErrorText(body: {
  error?: string;
  message?: string;
  details?: Record<string, unknown>;
}): string {
  const details = body.details ?? {};
  switch (body.error) {
    case 'not_found':
      return 'This run is not in the store, so there is nothing to re-grade.';
    case 'source_not_finished': {
      const status = typeof details.status === 'string' ? details.status : 'running';
      return details.stalled === true
        ? `This run is still marked ${status}, but it has stalled. The eval sweep closes it as incomplete at its next pass; re-grade it then.`
        : `This run is still ${status}: re-grade it once it has finished.`;
    }
    case 'source_not_regradable':
      return 'A calibration re-grade cannot be graded again: its replies come from many runs. Re-grade its calibration set instead.';
    case 'no_results': {
      const stored = typeof details.stored === 'number' ? details.stored : null;
      return stored === 0 || stored === null
        ? 'Nothing to re-grade: this run holds no replies.'
        : `Nothing to re-grade: none of this run's ${plural(stored, 'reply is', 'replies are')} of a case the eval still has.`;
    }
    case 'unknown_model':
      return `${typeof body.message === 'string' && body.message.length > 0 ? body.message : 'A judge id is not one the eval can run'}. A judge id names its vendor, such as openrouter:openai/gpt-5.5 or anthropic:claude-opus-5-5.`;
    case 'invalid_launch':
      return `The re-grade is not valid: ${body.message ?? 'check the form'}.`;
    case 'audit_unavailable':
      return 'The re-grade could not be audited, so nothing was launched.';
    default:
      // The daily cap, the test split and blocked cases, in a re-grade's words.
      return launchErrorText(body, 'regrade');
  }
}
